/// <amd-module name="DirtyDocumentServices/Services/BeforePageSwitchService" />
import * as Resources from "DirtyDocumentServices/Resources";
import * as React from "react";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { DirtyDocumentServiceName, IDirtyDocumentService } from "./DirtyDocumentService";

export const BeforePageSwitchServiceName = "BeforePageSwitchService";

export interface IBeforePageSwitchService extends IVssService {
    /**
     * Manually trigger the dirty document prompt
     * @param onLeavePage onLeavePage callback
     * @param documentId optional documentId if closing a specific document
     */
    warnBeforePageSwitch(onLeavePage: () => void, documentId?: string): void;
}

/**
 * Service for interrupting page navigations (either browser or Fast Page Switch)
 * This service listens to navigations, checks the dirty document status, and displays a
 * dialog if the document is dirty to confirm the navigation.
 */
export class BeforePageSwitchService extends VssService implements IBeforePageSwitchService {
    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        window.removeEventListener("beforeunload", this._onBeforeUnload);
        document.body.removeEventListener("fpsStarting", this._onFpsStarting);
    }

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        document.body.addEventListener("fpsStarting", this._onFpsStarting);
        window.addEventListener("beforeunload", this._onBeforeUnload);
    }

    public warnBeforePageSwitch(onLeavePage: () => void, documentId?: string): void {
        const dirtyDocumentService = this.pageContext.getService<IDirtyDocumentService>(DirtyDocumentServiceName);
        if (dirtyDocumentService.isDocumentDirty(documentId)) {
            const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(onDismissWarningPrompt =>
                this._getDialog(Resources.UnsavedChangesTitle, Resources.UnsavedChangesMessage, onDismissWarningPrompt, () => {
                    onDismissWarningPrompt();
                    onLeavePage();
                })
            );
        } else {
            onLeavePage();
        }
    }

    private _onBeforeUnload = (ev: BeforeUnloadEvent): string | undefined => {
        const dirtyDocumentService = this.pageContext.getService<IDirtyDocumentService>(DirtyDocumentServiceName);
        if (dirtyDocumentService.isDocumentDirty()) {
            // It doesn't matter what this string contains
            ev.returnValue = Resources.UnsavedChangesTitle;
            return ev.returnValue;
        }

        return undefined;
    };

    private _onFpsStarting = (ev: CustomEvent): void => {
        const dirtyDocumentService = this.pageContext.getService<IDirtyDocumentService>(DirtyDocumentServiceName);
        if (dirtyDocumentService.isDocumentDirty()) {
            ev.preventDefault();
            ev.detail.cancellationReason = Resources.UnsavedChangesTitle;

            const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(onDismiss =>
                this._getDialog(Resources.UnsavedChangesTitle, Resources.UnsavedChangesMessage, onDismiss, () =>
                    this._leaveButtonHandler(ev, onDismiss)
                )
            );
        }
    };

    private _getDialog = (title: string, message: string, onDismiss: () => void, onLeavePage: () => void): JSX.Element => {
        return (
            <Dialog
                titleProps={{ text: title }}
                lightDismiss={false}
                onDismiss={onDismiss}
                footerButtonProps={[
                    {
                        text: Resources.Stay,
                        onClick: onDismiss
                    },
                    {
                        text: Resources.Leave,
                        primary: true,
                        onClick: onLeavePage
                    }
                ]}
            >
                <div>{message}</div>
            </Dialog>
        );
    };

    private _leaveButtonHandler = (e: CustomEvent, onDismiss: () => void) => {
        const dirtyDocumentService = this.pageContext.getService<IDirtyDocumentService>(DirtyDocumentServiceName);
        dirtyDocumentService.unregisterAllHandlers();

        onDismiss();
        FastPageSwitch(e.detail.pageContext, e.detail.href, e.detail.recordHistoryEntry, e.detail.data);
    };
}

Services.add(BeforePageSwitchServiceName, { serviceFactory: BeforePageSwitchService });
